import { useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import UrgentMessageDisplay from "../../components/UrgentMessageDisplay";
import { getUrl } from "../../helpers/api/api";
import { pcoOtherEvents_endpoint } from "../../helpers/api/apiConstants";
import { momentFromIso } from "../../helpers/dateHelper";
import { useAppSelector } from "../../app/hooks";
import { selectLanguage } from "../../app/slices/languageSlice";
import {
  eventsPageHeadingString,
  eventsPageIntroString,
  noUpcomingEventsString,
} from "../../constants";

// event.startsAt/endsAt come back as ISO strings from the backend. A
// multi-day event (a weekend conference, say) shows as a date range
// instead of a single day/time.
const formatEventDateRange = (startsAt, endsAt) => {
  if (!startsAt) return "";
  const start = momentFromIso(startsAt);
  const end = endsAt ? momentFromIso(endsAt) : null;

  if (!end || start.isSame(end, "day")) {
    return start.format("ddd, MMM D · h:mm A");
  }
  if (start.year() === end.year()) {
    return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
  }
  return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
};

const Events = () => {
  const theme = useTheme();
  const language = useAppSelector(selectLanguage);
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(getUrl(pcoOtherEvents_endpoint))
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const eyebrowStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.brand.brass,
    margin: "0 0 6px",
  };

  const headingStyle = {
    fontFamily: theme.typography.secondaryFontFamily,
    fontWeight: 600,
    color: theme.palette.primary.dark,
    fontSize: "min(9vw, 40px)",
    margin: "0 0 12px",
  };

  const introStyle = {
    fontFamily: theme.typography.paragraphFontFamily,
    fontSize: "15px",
    lineHeight: 1.7,
    color: theme.palette.text.secondary,
    margin: "0 0 32px",
    maxWidth: "600px",
  };

  const cardStyle = {
    border: `1px solid ${theme.palette.brand.border}`,
    borderRadius: "10px",
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
  };

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.default,
        fontFamily: theme.typography.fontFamily,
        paddingTop: "var(--nav-height)",
        // calc, not a bare 100vh - see serviceTimes.js for why (paddingTop
        // above already reserves the header's own space).
        minHeight: "calc(100vh - var(--nav-height))",
      }}
    >
      <UrgentMessageDisplay page="Events" />

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "36px 24px 64px" }}>
        <div style={eyebrowStyle}>Events</div>
        <h1 style={headingStyle}>{eventsPageHeadingString(language)}</h1>
        <p style={introStyle}>{eventsPageIntroString(language)}</p>

        {loaded && events.length === 0 && (
          <div
            style={{
              fontFamily: theme.typography.paragraphFontFamily,
              fontSize: "14px",
              color: theme.palette.text.secondary,
              padding: "24px",
              backgroundColor: theme.palette.brand.linen,
              borderRadius: "10px",
            }}
          >
            {noUpcomingEventsString(language)}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "20px",
          }}
        >
          {events.map((event) => {
            // Cards for events with a Church Center sign-up link are
            // clickable through to it; everything else stays a plain,
            // non-interactive card.
            const CardTag = event.registrationUrl ? "a" : "div";
            const linkProps = event.registrationUrl
              ? { href: event.registrationUrl, target: "_blank", rel: "noreferrer" }
              : {};

            return (
              <CardTag
                key={event.id}
                {...linkProps}
                style={{
                  ...cardStyle,
                  textDecoration: "none",
                  cursor: event.registrationUrl ? "pointer" : "default",
                }}
              >
                <div
                  style={{
                    aspectRatio: "16 / 9",
                    backgroundColor: theme.palette.brand.linen,
                    backgroundImage: event.imageUrl ? `url(${event.imageUrl})` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {!event.imageUrl && (
                    <EventIcon sx={{ fontSize: "40px", color: theme.palette.brand.brass }} />
                  )}
                </div>
                <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
                  <div
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: theme.palette.primary.main,
                    }}
                  >
                    {formatEventDateRange(event.startsAt, event.endsAt)}
                  </div>
                  <div
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: "17px",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {event.title}
                  </div>
                  {event.description && (
                    <p
                      style={{
                        fontFamily: theme.typography.paragraphFontFamily,
                        fontSize: "13px",
                        lineHeight: 1.6,
                        color: theme.palette.text.secondary,
                        margin: "4px 0 0",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {event.description}
                    </p>
                  )}
                  {event.registrationUrl && (
                    <div
                      style={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: "12px",
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        marginTop: "auto",
                        paddingTop: "8px",
                      }}
                    >
                      Sign Up →
                    </div>
                  )}
                </div>
              </CardTag>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Events;
